<template>
  <TrendsViewer
    v-if="selectedReport"
    :report="selectedReport"
    :admin-id="adminId"
    :admin-name="adminName"
    @cancel="selectedReport = null"
    @reviewed="replaceReport"
    @status-saved="replaceReport"
  />

  <section v-else class="trends-window">
    <section class="trend-dashboard" aria-labelledby="trend-dashboard-title">
      <div class="trend-dashboard-heading">
        <div>
          <span class="trend-dashboard-eyebrow">Watch Intelligence</span>
          <h2 id="trend-dashboard-title">Trend Dashboard</h2>
        </div>

        <span class="trend-dashboard-note">Seed analysis will appear here.</span>
      </div>

      <div class="trend-metric-grid">
        <article class="trend-metric-card">
          <span class="trend-metric-icon material-icons-outlined" aria-hidden="true">radar</span>
          <div>
            <span class="trend-metric-label">Active Signals</span>
            <strong>&mdash;</strong>
            <small>Across all Watch activity</small>
          </div>
        </article>

        <article class="trend-metric-card">
          <span class="trend-metric-icon material-icons-outlined" aria-hidden="true">new_releases</span>
          <div>
            <span class="trend-metric-label">New This Week</span>
            <strong>&mdash;</strong>
            <small>Recently detected signals</small>
          </div>
        </article>

        <article class="trend-metric-card">
          <span class="trend-metric-icon material-icons-outlined" aria-hidden="true">trending_up</span>
          <div>
            <span class="trend-metric-label">Rising Topics</span>
            <strong>&mdash;</strong>
            <small>Increasing report activity</small>
          </div>
        </article>

        <article class="trend-metric-card">
          <span class="trend-metric-icon material-icons-outlined" aria-hidden="true">visibility</span>
          <div>
            <span class="trend-metric-label">Watchlist Reports</span>
            <strong>&mdash;</strong>
            <small>Guided Watchlist sessions</small>
          </div>
        </article>

        <article class="trend-metric-card">
          <span class="trend-metric-icon material-icons-outlined" aria-hidden="true">forum</span>
          <div>
            <span class="trend-metric-label">Watch Chat Reports</span>
            <strong>&mdash;</strong>
            <small>Client-led Watch conversations</small>
          </div>
        </article>

        <article class="trend-metric-card">
          <span class="trend-metric-icon material-icons-outlined" aria-hidden="true">priority_high</span>
          <div>
            <span class="trend-metric-label">Review Alerts</span>
            <strong>&mdash;</strong>
            <small>Signals needing attention</small>
          </div>
        </article>
      </div>
    </section>

    <div class="trend-dashboard-divider" aria-hidden="true"></div>

    <div class="trends-toolbar">
      <div class="toolbar-primary">
        <label class="trend-search">
          <span class="material-icons-outlined" aria-hidden="true">search</span>
          <input
            v-model="searchText"
            type="search"
            placeholder="Search Watch trends"
            aria-label="Search Watchlist reports"
          />
        </label>

        <button type="button" class="refresh-button" :disabled="loading" @click="loadReports">
          {{ loading ? "Loading..." : "Refresh" }}
        </button>
      </div>

      <div class="toolbar-filters">
        <select v-model="statusFilter" aria-label="Filter Watch trends by status">
          <option value="">All statuses</option>
          <option v-for="status in workflowStatuses" :key="status.value" :value="status.value">
            {{ status.label }}
          </option>
        </select>

        <select v-model="activityFilter" aria-label="Filter Watch trends by review activity">
          <option value="">All activity</option>
          <option value="updated">Needs review or updated</option>
        </select>

        <select v-model="groupBy" aria-label="Group Watch trends">
          <option value="date">Group by date</option>
          <option value="watchlist">Group by Watchlist</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="state-card">Loading Watchlist reports...</div>
    <div v-else-if="errorMessage" class="state-card error">{{ errorMessage }}</div>
    <div v-else-if="reports.length === 0" class="state-card">
      No completed Watchlist reports were found.
    </div>
    <div v-else-if="reportGroups.length === 0" class="state-card">
      No Watchlist reports match the selected filters.
    </div>

    <div v-else class="trend-groups">
      <section v-for="group in reportGroups" :key="group.id" class="trend-group">
        <button
          v-if="group.kind === 'watchlist'"
          type="button"
          class="watchlist-group-heading"
          :class="{ expandable: group.collapsible }"
          :disabled="!group.collapsible"
          :aria-expanded="isGroupExpanded(group)"
          @click="toggleGroup(group)"
        >
          <div>
            <h3>{{ group.title }}</h3>
            <small v-if="group.subtitle">{{ group.subtitle }}</small>
          </div>
          <span class="group-count">{{ group.reports.length }}</span>
          <span v-if="group.collapsible" class="material-icons-outlined" aria-hidden="true">
            {{ isGroupExpanded(group) ? "expand_less" : "expand_more" }}
          </span>
        </button>

        <div v-else class="date-heading">
          <h3>{{ group.title }}</h3>
          <span>{{ group.reports.length }}</span>
        </div>

        <div v-if="isGroupExpanded(group)" class="trend-table">
          <div class="trend-row table-heading" aria-hidden="true">
            <span>Watchlist</span>
            <span>Answers</span>
            <span>Completed</span>
            <span>Status</span>
          </div>

          <button
            v-for="report in group.reports"
            :key="report.id"
            type="button"
            class="trend-row"
            @click="selectedReport = report"
          >
            <div class="watchlist-summary">
              <strong>{{ report.name }}</strong>
              <span v-if="report.explainerTitle">{{ report.explainerTitle }}</span>
            </div>
            <span>{{ report.answerCount ?? "—" }}</span>
            <time :datetime="dateTime(report.completedAt)">{{ formattedDate(report.completedAt) }}</time>
            <div class="status-summary">
              <span class="status-pill" :class="report.status">{{ statusLabel(report.status) }}</span>
              <span v-if="isUpdatedReport(report)" class="updated-pill">
                {{ report.needsReview ? "Needs Review" : "Updated" }}
              </span>
            </div>
          </button>
        </div>
      </section>
    </div>
  </section>
</template>

<script>
import { computed, onMounted, ref } from "vue";

import { fetchWatchTrendReports } from "@/firebase/firestore";
import TrendsViewer from "@/components/trends/TrendsViewer.vue";
import {
  watchTrendWorkflowStatuses,
  watchTrendWorkflowStatusLabel
} from "@/models/watchTrends";

export default {
  name: "TrendsListWindow",

  components: { TrendsViewer },

  props: {
    adminId: { type: String, default: "" },
    adminName: { type: String, default: "" }
  },

  setup() {
    const reports = ref([]);
    const loading = ref(false);
    const errorMessage = ref("");
    const searchText = ref("");
    const statusFilter = ref("");
    const activityFilter = ref("");
    const groupBy = ref("date");
    const expandedGroupIds = ref(new Set());
    const selectedReport = ref(null);
    const workflowStatuses = watchTrendWorkflowStatuses;

    const dateValue = value => {
      if (value?.toDate) { return value.toDate(); }
      if (value instanceof Date) { return value; }
      return new Date(value ?? 0);
    };

    const isUpdatedReport = report => {
      if (report?.needsReview === true) { return true; }
      if (!report?.lastReviewedDate) { return true; }
      if (!report?.editedOnDate) { return false; }
      return dateValue(report.editedOnDate) > dateValue(report.lastReviewedDate);
    };

    const filteredReports = computed(() => {
      const search = searchText.value.trim().toLowerCase();

      return reports.value.filter(report => {
        const matchesSearch = !search || [
          report.name,
          report.explainerTitle,
          report.description
        ].some(value => String(value ?? "").toLowerCase().includes(search));
        const matchesStatus = !statusFilter.value || report.status === statusFilter.value;
        const matchesActivity = activityFilter.value !== "updated" || isUpdatedReport(report);

        return matchesSearch && matchesStatus && matchesActivity;
      });
    });

    const dateGroups = computed(() => {
      const now = new Date();
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const startOfWeek = new Date(startOfToday);
      startOfWeek.setDate(startOfToday.getDate() - startOfToday.getDay());
      const groups = [
        { id: "today", kind: "date", title: "Today", reports: [] },
        { id: "this-week", kind: "date", title: "This Week", reports: [] },
        { id: "older", kind: "date", title: "Older", reports: [] }
      ];

      filteredReports.value.forEach(report => {
        const completedAt = dateValue(report.completedAt);
        if (completedAt >= startOfToday) { groups[0].reports.push(report); }
        else if (completedAt >= startOfWeek) { groups[1].reports.push(report); }
        else { groups[2].reports.push(report); }
      });

      return groups.filter(group => group.reports.length);
    });

    const watchlistGroups = computed(() => {
      const grouped = new Map();

      filteredReports.value.forEach(report => {
        const key = report.watchlistId || report.name;
        if (!grouped.has(key)) {
          grouped.set(key, {
            id: `watchlist:${key}`,
            kind: "watchlist",
            title: report.name,
            subtitle: report.explainerTitle,
            reports: []
          });
        }
        grouped.get(key).reports.push(report);
      });

      return Array.from(grouped.values()).map(group => ({
        ...group,
        collapsible: group.reports.length > 1
      }));
    });

    const reportGroups = computed(() =>
      groupBy.value === "watchlist" ? watchlistGroups.value : dateGroups.value
    );

    const isGroupExpanded = group =>
      !group.collapsible || expandedGroupIds.value.has(group.id);

    const toggleGroup = group => {
      if (!group.collapsible) { return; }
      const next = new Set(expandedGroupIds.value);
      next.has(group.id) ? next.delete(group.id) : next.add(group.id);
      expandedGroupIds.value = next;
    };

    const loadReports = async () => {
      loading.value = true;
      errorMessage.value = "";
      try {
        reports.value = await fetchWatchTrendReports();
      } catch (error) {
        errorMessage.value = "Unable to load Watchlist trend reports.";
      } finally {
        loading.value = false;
      }
    };

    const replaceReport = updatedReport => {
      reports.value = reports.value.map(report =>
        report.id === updatedReport.id ? updatedReport : report
      );
      selectedReport.value = updatedReport;
    };

    const formattedDate = value => new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit"
    }).format(dateValue(value));

    const dateTime = value => dateValue(value).toISOString();

    onMounted(loadReports);

    return {
      reports,
      loading,
      errorMessage,
      searchText,
      statusFilter,
      activityFilter,
      groupBy,
      selectedReport,
      workflowStatuses,
      reportGroups,
      isUpdatedReport,
      isGroupExpanded,
      toggleGroup,
      loadReports,
      replaceReport,
      formattedDate,
      dateTime,
      statusLabel: watchTrendWorkflowStatusLabel
    };
  }
};
</script>

<style scoped>
.trends-window { display: flex; flex-direction: column; height: 100%; min-height: 0; }
.trend-dashboard { display: flex; flex-direction: column; gap: 14px; }
.trend-dashboard-heading { display: flex; align-items: flex-end; justify-content: space-between; gap: 20px; }
.trend-dashboard-eyebrow { color: #159c62; font-size: 0.68rem; font-weight: 800; letter-spacing: 0.12em; text-transform: uppercase; }
.trend-dashboard-heading h2 { margin: 3px 0 0; color: #1d2924; font-size: 1.28rem; }
.trend-dashboard-note { color: #7b8580; font-size: 0.8rem; }
.trend-metric-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
.trend-metric-card { display: flex; align-items: center; gap: 13px; min-width: 0; min-height: 82px; padding: 15px 16px; border: 1px solid #dde4e0; border-radius: 12px; background: #fff; box-shadow: 0 5px 18px rgba(29, 49, 39, 0.04); }
.trend-metric-card > div { min-width: 0; }
.trend-metric-icon { display: inline-flex; align-items: center; justify-content: center; width: 40px; height: 40px; flex: 0 0 40px; border-radius: 10px; background: #edf7f1; color: #178554; }
.trend-metric-label { display: block; overflow: hidden; color: #68736d; font-size: 0.74rem; font-weight: 800; text-overflow: ellipsis; white-space: nowrap; }
.trend-metric-card strong { display: block; margin-top: 2px; color: #1d2924; font-size: 1.35rem; line-height: 1; }
.trend-metric-card small { display: block; margin-top: 5px; overflow: hidden; color: #87908b; font-size: 0.7rem; text-overflow: ellipsis; white-space: nowrap; }
.trend-dashboard-divider { height: 1px; margin: 20px 0; background: #e1e6e3; }
.trends-toolbar { display: flex; flex-direction: column; gap: 12px; margin-bottom: 18px; }
.toolbar-primary { display: grid; grid-template-columns: minmax(260px, 1fr) auto; gap: 12px; }
.toolbar-filters { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
.trend-search { display: flex; align-items: center; gap: 10px; min-width: 0; padding: 0 14px; border: 1px solid #d8dedb; border-radius: 10px; background: #fff; color: #7b8580; }
.trend-search input { width: 100%; padding: 12px 0; border: 0; outline: 0; background: transparent; color: #18231f; font: inherit; }
select, .refresh-button { padding: 12px 14px; border: 1px solid #d8dedb; border-radius: 10px; background: #fff; color: #29342f; font: inherit; }
.refresh-button { min-width: 130px; font-weight: 600; cursor: pointer; }
.refresh-button:disabled { cursor: wait; opacity: 0.65; }
.state-card { padding: 24px; border: 1px solid #dde2df; border-radius: 12px; background: #fff; color: #66716b; text-align: center; }
.state-card.error { border-color: #edc4c4; color: #a33636; }
.trend-groups { min-height: 0; padding-right: 4px; overflow-y: auto; }
.trend-group + .trend-group { margin-top: 26px; }
.date-heading { display: flex; align-items: center; gap: 9px; margin-bottom: 9px; }
.date-heading h3 { margin: 0; color: #68736d; font-size: 0.74rem; letter-spacing: 0.1em; text-transform: uppercase; }
.date-heading span, .group-count { min-width: 22px; padding: 4px 7px; border-radius: 999px; background: #edf4ef; color: #178554; font-size: 0.72rem; font-weight: 700; text-align: center; }
.watchlist-group-heading { display: grid; grid-template-columns: minmax(0, 1fr) auto auto; align-items: center; gap: 12px; width: 100%; margin-bottom: 9px; padding: 13px 16px; border: 1px solid #dfe5e1; border-radius: 11px; background: #fff; color: #1d2924; text-align: left; }
.watchlist-group-heading.expandable { cursor: pointer; }
.watchlist-group-heading h3 { margin: 0; font-size: 1rem; }
.watchlist-group-heading small { display: block; margin-top: 3px; color: #7b8580; }
.trend-table { overflow: hidden; border: 1px solid #dde2df; border-radius: 12px; background: #fff; }
.trend-row { display: grid; grid-template-columns: minmax(260px, 1.8fr) 0.55fr 1fr 1fr; align-items: center; gap: 20px; width: 100%; padding: 17px 20px; border: 0; background: #fff; color: #29342f; text-align: left; }
button.trend-row { cursor: pointer; }
button.trend-row:hover { background: #f8faf9; }
.trend-row + .trend-row { border-top: 1px solid #edf0ee; }
.table-heading { background: #f7f9f8; color: #68736d; font-size: 0.73rem; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; }
.watchlist-summary { display: flex; flex-direction: column; gap: 4px; }
.watchlist-summary strong { color: #1d2924; }
.watchlist-summary span, time { color: #7b8580; font-size: 0.84rem; }
.status-summary { display: flex; align-items: flex-start; flex-wrap: wrap; gap: 6px; }
.status-pill, .updated-pill { padding: 6px 9px; border-radius: 999px; font-size: 0.72rem; font-weight: 700; }
.status-pill { background: #edf4ef; color: #178554; }
.status-pill.inReview { background: #fff2e7; color: #ad5514; }
.status-pill.monitoring { background: #e6f3f7; color: #22667a; }
.status-pill.patternIdentified { background: #fff5d8; color: #8b6510; }
.status-pill.escalated { background: #eee9fb; color: #65479b; }
.status-pill.closed { background: #eef0ef; color: #66716b; }
.updated-pill { background: #fff0f0; color: #a33636; }
@media (max-width: 1100px) { .trend-metric-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 900px) { .toolbar-filters, .toolbar-primary { grid-template-columns: 1fr; } .trend-row { grid-template-columns: 1fr; gap: 7px; } .table-heading { display: none; } }
@media (max-width: 620px) { .trend-dashboard-heading { align-items: flex-start; flex-direction: column; gap: 6px; } .trend-metric-grid { grid-template-columns: 1fr; } }
</style>
